import React, { useState, useEffect } from 'react';
import { Box, Text, VStack, HStack, Badge, Flex, useBreakpointValue, Image, Icon, Checkbox, Tooltip, Input, Button, IconButton, Link as ChakraLink, useToast } from '@chakra-ui/react';
import { CalendarIcon, TimeIcon, WarningIcon, ExternalLinkIcon, EditIcon } from '@chakra-ui/icons';
import ProdutoImagem from "../ProdutoImagem";
import { FaUser, FaCommentDots, FaFileInvoice, FaTruck, FaBoxes, FaPlay, FaWarehouse, FaImage, FaStore, FaCheckCircle, FaTruckLoading } from 'react-icons/fa';
import { setLinkLalamove } from "../../services/api";
import ModalSolicitacaoLalamove from "../ModalSolicitacaoLalamove";
import { isColetaMl } from '../../utils/envioMl';

const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return 'Data inválida';
    }
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).replace(',', '');
};

export const getProblemLabels = (product) => {
    const details = product?.verificationDetails;
    if (!details) return [];
    const labels = [];
    const esperado = parseInt(product?.quantity, 10);
    const conferido = parseInt(details.checkedQuantity, 10);
    if (!isNaN(conferido) && !isNaN(esperado) && conferido !== esperado) labels.push('QUANTIDADE');
    if (details.issues?.photo) labels.push('FOTO');
    if (details.issues?.name) labels.push('NOME');
    if (details.issues?.code) labels.push('CÓDIGO');
    if (details.issues?.brand) labels.push('MARCA');
    if (details.issues?.avaria) labels.push('AVARIA');
    return labels;
};

const PROBLEM_COLORS = {
    QUANTIDADE: 'orange',
    FOTO: 'purple',
    NOME: 'blue',
    'CÓDIGO': 'cyan',
    MARCA: 'teal',
    AVARIA: 'red',
};

// Estilo base das etiquetinhas
const tagStyle = {
    fontSize: "9px",
    px: 2,
    py: 0.5,
    borderTopRadius: "md",
    borderBottomRadius: "0",
    display: "flex",
    alignItems: "center",
    boxShadow: "sm",
    lineHeight: "1.2",
    fontWeight: "bold"
};

/**
 * Etiqueta de retirada na loja, ao lado de NFE/ETIQ. Só aparece em venda do
 * site com frete de retirada e quando o pai passa onProntoRetirada (hoje, as
 * colunas Verificados e Embalados).
 *
 * A liberação é automática: fechou a conferência de todos os itens, o cliente
 * é avisado 5 minutos depois. Verde = liberado, clicar desfaz. Amarela
 * desabilitada = a venda tem item em outra coluna, então a conferência ainda
 * não fechou. As travas são refeitas no backend.
 *
 * Concluir a retirada fica só no modal de processamento: aqui a linha de
 * etiquetas é curta e uma segunda badge passava por cima do PROCESSAR.
 */
export const RetiradaBadge = ({ retiradaLoja, prontoRetiradaAt, clienteAvisado, isBlocked, onClick }) => {
    if (!retiradaLoja || !onClick) return null;

    const marcado = !!prontoRetiradaAt;
    const impedimento = isBlocked
        ? 'Venda com item em outra etapa: confira todos para liberar a retirada'
        : null;

    const bloqueado = !marcado && !!impedimento;

    const titulo = marcado
        ? (clienteAvisado
            ? 'Cliente já avisado. Clique para desfazer a liberação'
            : 'Liberado. O cliente é avisado em até 5 minutos. Clique para desfazer')
        : (impedimento || 'Libera sozinho quando todos os itens forem conferidos. Clique para liberar agora');

    return (
        <Tooltip label={titulo} placement="top" hasArrow>
            <Badge
                {...tagStyle}
                variant="solid"
                colorScheme={marcado ? 'green' : bloqueado ? 'yellow' : 'orange'}
                color={bloqueado ? 'yellow.900' : 'white'}
                cursor={bloqueado ? 'not-allowed' : 'pointer'}
                opacity={bloqueado ? 0.8 : 1}
                _hover={bloqueado ? {} : { transform: 'scale(1.05)' }}
                transition="all 0.1s"
                onClick={(e) => {
                    e.stopPropagation();
                    if (!bloqueado) onClick();
                }}
            >
                <Icon as={marcado ? FaCheckCircle : FaStore} mr={0.5} boxSize={2.5} />
                RETIRADA
            </Badge>
        </Tooltip>
    );
};

export const OrderCardDespacho = ({
    product,
    onOpenModal,
    borderColor,
    isSelectable,
    isSelected,
    onToggleSelect,
    isMultiItemVenda,
    vendaItemCount,
    isIncompleteVenda,
    onProcessIndividual,
    onLinkLalamoveSalvo,
    etapaAtual,
    onDarEntradaEstoque,
    onCorrigirImagem,
    onProntoRetirada,
    inGroup = false,
    showProblemTags = false,
    kitPacks
}) => {
    const problemLabels = showProblemTags ? getProblemLabels(product) : [];
    const imagemCorrigida = showProblemTags && !!product?.verificationDetails?.fotoCorrigidaAt;
    const kitInfo = (kitPacks && product.packId) ? kitPacks[product.packId] : null;
    const isMobile = useBreakpointValue({ base: true, md: false });
    const isLalamove = String(product?.envio?.modo_envio || '').toUpperCase().includes('LALAMOVE');
    const etiquetaOk = product.etiquetaEmitida || isLalamove;
    const linkRastreioSalvo = product?.envio?.link_rastreio || '';
    const toast = useToast();
    const [editandoLink, setEditandoLink] = useState(false);
    const [modalLalamoveAberto, setModalLalamoveAberto] = useState(false);
    const [linkInput, setLinkInput] = useState(linkRastreioSalvo);
    const [salvandoLink, setSalvandoLink] = useState(false);

    useEffect(() => {
        setLinkInput(linkRastreioSalvo);
        setEditandoLink(false);
    }, [linkRastreioSalvo]);

    const abrirModalLalamove = (e) => {
        if (e) e.stopPropagation();
        setModalLalamoveAberto(true);
    };

    const handleSalvarLink = async (e) => {
        if (e) e.stopPropagation();
        const link = linkInput.trim();
        if (!link) {
            toast({ title: 'Informe o link do Lalamove', status: 'warning', duration: 2500 });
            return;
        }
        if (!/^https?:\/\//i.test(link)) {
            toast({ title: 'Link deve começar com http:// ou https://', status: 'warning', duration: 3000 });
            return;
        }
        setSalvandoLink(true);
        const res = await setLinkLalamove(product.vendaId, link);
        setSalvandoLink(false);
        if (res?.success) {
            toast({ title: 'Link de rastreio salvo', status: 'success', duration: 2500 });
            setEditandoLink(false);
            if (onLinkLalamoveSalvo) onLinkLalamoveSalvo(product.vendaId, link);
        } else {
            toast({ title: res?.error || 'Erro ao salvar link', status: 'error', duration: 3500 });
        }
    };

    if (!product) return null;

    const handleCardClick = (e) => {
        if (e) e.stopPropagation();
        onOpenModal();
    };

    const handleHeaderClick = (e) => {
        if (e) e.stopPropagation();
        if (isSelectable && onToggleSelect) {
            onToggleSelect(product);
        } else {
            onOpenModal();
        }
    };

    const handleQuickProcess = (e) => {
        e.stopPropagation();
        if (onProcessIndividual) {
            onProcessIndividual(product);
        }
    };

    const envio = product.envio || {};
    const isFlex = envio.flex_ml === 1;
    const isColeta = isColetaMl(envio);
    const modoEnvio = envio.modo_envio;

    const FlexLogo = () => (
        <Box mr={1} w={{ base: "25px", md: "40px", lg: "30px" }}>
            <Image
                src="/logo_flex_ml.webp"
                alt="Logo Flex Mercado Livre"
                w={"100%"}
                h={{ base: "25px", md: "25px" }}
                objectFit="contain"
                borderRadius="full"
            />
        </Box>
    );

    const ShippingBadge = () => (
        modoEnvio ? (
            <Badge colorScheme="blue" variant="subtle" fontSize="0.4em">
                {modoEnvio}
            </Badge>
        ) : null
    );

    const ColetaBadge = () => (
        <Badge
            colorScheme="pink"
            variant="solid"
            px={2}
            py={0.5}
            borderRadius="md"
            display="flex"
            alignItems="center"
            title="Coleta: a transportadora do Mercado Livre retira o pacote aqui na TAGA"
        >
            <Icon as={FaTruckLoading} mr={1} boxSize={3} />
            COLETA
        </Badge>
    );

    const isBlocked = isSelectable && isIncompleteVenda;

    return (
        <Box
            position="relative"
            mt={!inGroup && (isSelectable || product.verificationDetails?.status === 5) ? 4 : 0}
            transition="all 0.2s"
            _hover={isBlocked ? {} : { transform: 'translateY(-4px)' }}
        >            <HStack
            spacing={0.5}
            position="absolute"
            top="-14px"
            left="8px"
            zIndex={1}
        >
                {/* Etiquetas flutuantes - Lado Esquerdo */}
                {isSelectable && !inGroup && (
                    <>
                        {/* Tag NFe */}
                        <Badge
                            {...tagStyle}
                            variant="solid"
                            colorScheme={product.nfeEmitida ? "green" : "yellow"}
                            color={product.nfeEmitida ? "white" : "yellow.900"}
                        >
                            <Icon as={FaFileInvoice} mr={0.5} boxSize={2.5} />
                            NFE
                        </Badge>

                        {/* Tag Etiqueta */}
                        <Badge
                            {...tagStyle}
                            variant="solid"
                            colorScheme={etiquetaOk ? (isLalamove ? "purple" : "green") : "yellow"}
                            color={etiquetaOk ? "white" : "yellow.900"}
                        >
                            <Icon as={FaTruck} mr={0.5} boxSize={2.5} />
                            {isLalamove ? "LALAMOVE" : "ETIQ"}
                        </Badge>

                        {/* Tag Venda Multi-item */}
                        {isMultiItemVenda && (
                            <Badge
                                {...tagStyle}
                                variant="solid"
                                colorScheme="purple"
                            >
                                <Icon as={FaBoxes} mr={0.5} boxSize={2.5} />
                                ({vendaItemCount})
                            </Badge>
                        )}

                        <RetiradaBadge
                            retiradaLoja={product.retiradaLoja}
                            prontoRetiradaAt={product.prontoRetiradaAt}
                            clienteAvisado={product.clienteAvisadoRetirada}
                            isBlocked={isBlocked}
                            onClick={onProntoRetirada ? () => onProntoRetirada(product) : null}
                        />
                    </>
                )}
            </HStack>

            {/* Tag de Ação Rápida - Lado Direito */}
            {isSelectable && !isBlocked && !inGroup && (
                <Box
                    position="absolute"
                    top="-22px"
                    right="8px"
                    zIndex={2}
                >
                    <Badge
                        fontSize="11px"
                        px={4}
                        py={1}
                        borderTopRadius="md"
                        borderBottomRadius="0"
                        display="flex"
                        alignItems="center"
                        boxShadow="md"
                        fontWeight="bold"
                        lineHeight="1.2"
                        bg="blue.500"
                        color="white"
                        cursor="pointer"
                        _hover={{ bg: "blue.600", transform: "scale(1.05)" }}
                        transition="all 0.1s"
                        onClick={handleQuickProcess}
                    >
                        <Icon as={FaPlay} mr={1} boxSize={2.5} />
                        PROCESSAR
                    </Badge>
                </Box>
            )}

            <Box
                p={3}
                borderWidth="2px"
                borderRadius="lg"
                borderColor={isBlocked ? 'red.400' : (product.verificationDetails?.status === 5 ? 'red.400' : borderColor)}
                borderStyle={isBlocked ? 'dashed' : 'solid'}
                bg={isBlocked ? 'red.50' : (product.verificationDetails?.status === 5 ? 'red.50' : 'white')}
                boxShadow="sm"
                transition="all 0.2s"
                opacity={isBlocked ? 0.75 : 1}
                position="relative"
            >
                {isBlocked && (
                    <Badge colorScheme="red" position="absolute" top={-2} right={2} fontSize="xs" px={2} py={0.5} borderRadius="full" zIndex={1}>
                        ⚠️ Incompleta
                    </Badge>
                )}

                <VStack align="stretch" spacing={2}>
                    {kitInfo && !inGroup && (
                        <Box bg="orange.400" color="white" px={2} py={1} borderRadius="md" boxShadow="sm">
                            <HStack spacing={1} align="center">
                                <Icon as={FaBoxes} boxSize={3} />
                                <Text fontSize="11px" fontWeight="bold" lineHeight="1.2">
                                    KIT • {kitInfo.count} pedidos vinculados — despache juntos
                                </Text>
                            </HStack>
                            <Text fontSize="10px" fontWeight="normal" lineHeight="1.3" wordBreak="break-all" mt={0.5}>
                                Pack {product.packId} • {kitInfo.orderIds.join(', ')}
                            </Text>
                        </Box>
                    )}
                    <Box onClick={handleHeaderClick} cursor="pointer" rounded="md" _hover={isSelectable ? { bg: 'blue.50' } : {}}>
                        {isMobile ? (
                            <VStack align="stretch" spacing={2}>
                                <HStack justify="space-between" align="flex-start">
                                    <VStack align="flex-start" spacing={0} flex={1} pr={2}>
                                        <HStack spacing={2} align="center">
                                            {isSelectable && (
                                                <Checkbox
                                                    isChecked={isSelected}
                                                    isDisabled={isBlocked}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        onToggleSelect && onToggleSelect(product);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    size="md"
                                                    colorScheme="blue"
                                                />
                                            )}
                                            <Text fontSize="12px" fontWeight="bold" color="gray.700" whiteSpace="normal" wordBreak="break-all">
                                                {product?.orderId ? product?.orderId : product?.vendaId}
                                            </Text>
                                        </HStack>
                                        {product.packId && (
                                            <Text fontSize="xs" color="gray.500" whiteSpace="normal" wordBreak="break-all">
                                                Pack: {product.packId}
                                            </Text>
                                        )}
                                    </VStack>

                                    <VStack align="flex-end" spacing={1}>
                                        <HStack spacing={1} wrap="wrap" justify="flex-end">
                                            {isFlex && <FlexLogo />}
                                            {isColeta && <ColetaBadge />}
                                            <Badge colorScheme="teal" variant="outline">
                                                {product.fornecedor}
                                            </Badge>
                                        </HStack>
                                        <ShippingBadge />
                                    </VStack>
                                </HStack>
                                {product.estoque_taga > 0 && (
                                    <Badge colorScheme="red" variant="solid" px={2} py={1} borderRadius="md" w="100%" textAlign="center">
                                        <VStack align="center" spacing={0}>
                                            <Text fontSize="0.9em" fontWeight="bold" lineHeight="1.2">
                                                ({product.estoque_taga}) ESTOQUE TAGA
                                            </Text>
                                            <Text fontSize="0.9em" fontStyle="italic" lineHeight="1">
                                                ({product.localizacao_estoque})
                                            </Text>
                                        </VStack>
                                    </Badge>
                                )}
                            </VStack>

                        ) : (
                            <HStack justify="space-between" align="flex-start">
                                <VStack align="flex-start" spacing={0} flex={1} pr={2}>
                                    <HStack spacing={2} align="center">
                                        {isSelectable && (
                                            <Checkbox
                                                isChecked={isSelected}
                                                isDisabled={isBlocked}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    onToggleSelect && onToggleSelect(product);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                size="md"
                                                colorScheme="blue"
                                            />
                                        )}
                                        <Text fontSize="12px" fontWeight="bold" color="gray.700" whiteSpace="normal" wordBreak="break-all">
                                            {product.orderId || product.vendaId}
                                        </Text>
                                    </HStack>
                                    {product.packId && (
                                        <Text fontSize="xs" color="gray.500" whiteSpace="normal" wordBreak="break-all">
                                            Pack: {product.packId}
                                        </Text>
                                    )}
                                </VStack>

                                <VStack align="flex-end" spacing={1} maxW="60%">
                                    <HStack spacing={1} wrap="wrap" justify="flex-end">
                                        <ShippingBadge />
                                        <Badge colorScheme="teal" variant="outline">
                                            {product.fornecedor}
                                        </Badge>
                                    </HStack>

                                    {(product.estoque_taga > 0 || isFlex || isColeta) && (
                                        <HStack spacing={1} wrap="wrap" justify="flex-end">
                                            {product.estoque_taga > 0 && (
                                                <Badge colorScheme="red" variant="solid" px={2} py={1} borderRadius="md" textAlign="center">
                                                    <VStack align="center" spacing={0}>
                                                        <Text fontSize="0.75em" fontWeight="bold" lineHeight="1.2">
                                                            ({product.estoque_taga}) TAGA
                                                        </Text>
                                                        <Text fontSize="0.7em" fontStyle="italic" lineHeight="1">
                                                            ({product.localizacao_estoque})
                                                        </Text>
                                                    </VStack>
                                                </Badge>
                                            )}
                                            {isFlex && <FlexLogo />}
                                            {isColeta && <ColetaBadge />}
                                        </HStack>
                                    )}
                                </VStack>
                            </HStack>
                        )}
                    </Box>

                    <Box onClick={handleCardClick} cursor="pointer">
                        {problemLabels.length > 0 && (
                            <HStack spacing={1} mb={2} flexWrap="wrap">
                                {imagemCorrigida && (
                                    <Badge colorScheme="green" variant="solid" fontSize="9px" px={2} py={0.5} borderRadius="full">
                                        IMAGEM CORRIGIDA
                                    </Badge>
                                )}
                                {problemLabels.map(label => (
                                    <Badge
                                        key={label}
                                        colorScheme={PROBLEM_COLORS[label] || 'gray'}
                                        variant="solid"
                                        fontSize="9px"
                                        px={2}
                                        py={0.5}
                                        borderRadius="full"
                                    >
                                        {label}
                                    </Badge>
                                ))}
                            </HStack>
                        )}
                        <HStack spacing={3} align="flex-start">
                            <VStack spacing={1}>
                                <ProdutoImagem
                                    produto={product.produtoEstoque}
                                    origem={null}
                                    width={"50px"}
                                    height={"50px"}
                                />
                                {product.preco_unitario && (
                                    <Text fontSize="xs" fontWeight="bold" color="green.600" mt={1}>
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(product.preco_unitario) * parseInt(product.quantity || 1))}
                                    </Text>
                                )}
                                {product.observacoes && product.observacoes.length > 0 && (
                                    <Badge colorScheme="orange" display="flex" alignItems="center" px={4} borderRadius="full" title="Possui observações">
                                        <Icon as={FaCommentDots} mr={1} />
                                        {product.observacoes.length}
                                    </Badge>
                                )}
                            </VStack>
                            <VStack align="stretch" spacing={1} flex={1}>
                                <Flex align="center" gap={2} wrap="wrap" justify="space-between" w="100%">
                                    <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color="gray.800"
                                        bg="gray.100"
                                        px={2.5}
                                        py={1.5}
                                        borderRadius="md"
                                        lineHeight="tight"
                                        whiteSpace="normal"
                                        wordBreak="break-word"
                                        flex={1}
                                    >
                                        {product.name}
                                    </Text>
                                    {product.verificationDetails?.status === 5 && (
                                        <Badge colorScheme="red" px={2} py={1} borderRadius="md" display="flex" alignItems="center">
                                            <Icon as={WarningIcon} mr={1} boxSize={3} /> RESSALVA
                                        </Badge>
                                    )}
                                </Flex>
                                <Flex wrap="wrap" columnGap={2} rowGap={1}>
                                    {product.id && (
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                            <Text as="span" fontWeight="bold" color="gray.600">SKU:</Text> {product.id}
                                        </Text>
                                    )}

                                    {product.globalCode && (
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                            <Text as="span" fontWeight="bold" color="gray.600">Global:</Text> {product.globalCode}
                                        </Text>
                                    )}

                                    {product?.produtoEstoque?.codigo_fornecedor && (
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap" title="Código do Fornecedor">
                                            <Text as="span" fontWeight="bold" color="gray.600">Forn:</Text> {product.produtoEstoque.codigo_fornecedor}
                                        </Text>
                                    )}

                                    {product?.produtoEstoque?.codigo_fabricante && (
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap" title="Código do Fabricante">
                                            <Text as="span" fontWeight="bold" color="gray.600">Fabr:</Text> {product.produtoEstoque.codigo_fabricante}
                                        </Text>
                                    )}

                                    {product.brand && (
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                            <Text as="span" fontWeight="bold" color="gray.600">Marca:</Text> {product.brand}
                                        </Text>
                                    )}

                                    {product?.produtoEstoque?.codigo_barra && (
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap" title="Código de Barras">
                                            <Text as="span" fontWeight="bold" color="gray.600">Cód. Barra:</Text> {product.produtoEstoque.codigo_barra}
                                        </Text>
                                    )}
                                </Flex>
                            </VStack>
                        </HStack>

                        <Flex
                            justify="space-between"
                            align="center"
                            pt={2}
                            mt={2}
                            borderTopWidth="1px"
                            borderColor="gray.200"
                            w="100%"
                            gap={2}
                        >
                            <Flex gap={2} flexShrink={0}>
                                <HStack spacing={1} align="center" title={`Data da Venda: ${formatDate(product.orderDate)}`}>
                                    <CalendarIcon color="gray.500" boxSize={3} />
                                    <Text fontSize="11px" color="gray.600">
                                        {formatDate(product.orderDate)}
                                    </Text>
                                </HStack>
                                <HStack spacing={1} align="center" title={`Limite de Despacho: ${formatDate(product.dataLimiteEnvio)}`}>
                                    <TimeIcon color="red.500" boxSize={3} />
                                    <Text fontSize="11px" color="red.600" fontWeight="bold">
                                        {formatDate(product.dataLimiteEnvio)}
                                    </Text>
                                </HStack>
                            </Flex>
                            {product.customerName && (
                                <HStack spacing={1} align="center" flex={1} minW={0} justify="flex-end" title={`Cliente: ${product.customerName}`}>
                                    <Icon as={FaUser} color="gray.500" boxSize={3} flexShrink={0} />
                                    <Text fontSize="11px" fontWeight="bold" color="gray.700" isTruncated>
                                        {product.customerName}
                                    </Text>
                                </HStack>
                            )}
                        </Flex>
                    </Box>

                    {isLalamove && etapaAtual === 'processamento' && (
                        <Box
                            mt={2}
                            p={2}
                            bg={linkRastreioSalvo && !editandoLink ? "purple.50" : "orange.50"}
                            borderWidth="1px"
                            borderColor={linkRastreioSalvo && !editandoLink ? "purple.200" : "orange.300"}
                            borderRadius="md"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button
                                size="xs"
                                colorScheme="purple"
                                leftIcon={<Icon as={FaTruck} boxSize={3} />}
                                w="100%"
                                mb={2}
                                onClick={abrirModalLalamove}
                            >
                                {linkRastreioSalvo ? 'Ver corrida Lalamove' : 'Pedir corrida Lalamove'}
                            </Button>

                            {linkRastreioSalvo && !editandoLink ? (
                                <HStack spacing={2} justify="space-between" wrap="wrap">
                                    <HStack spacing={1} minW={0} flex={1}>
                                        <Icon as={FaTruck} color="purple.600" boxSize={3} />
                                        <Text fontSize="11px" fontWeight="bold" color="purple.700" whiteSpace="nowrap">
                                            Lalamove:
                                        </Text>
                                        <ChakraLink
                                            href={linkRastreioSalvo}
                                            isExternal
                                            fontSize="11px"
                                            color="purple.700"
                                            isTruncated
                                            maxW="200px"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {linkRastreioSalvo} <ExternalLinkIcon boxSize={2.5} />
                                        </ChakraLink>
                                    </HStack>
                                    <IconButton
                                        aria-label="Editar link"
                                        icon={<EditIcon />}
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="purple"
                                        onClick={(e) => { e.stopPropagation(); setEditandoLink(true); }}
                                    />
                                </HStack>
                            ) : (
                                <VStack align="stretch" spacing={1}>
                                    <HStack spacing={1}>
                                        <Icon as={FaTruck} color="orange.500" boxSize={3} />
                                        <Text fontSize="11px" fontWeight="bold" color="orange.700">
                                            Link de rastreio Lalamove {linkRastreioSalvo ? '' : '(pendente)'}
                                        </Text>
                                    </HStack>
                                    <HStack spacing={2}>
                                        <Input
                                            size="xs"
                                            placeholder="Cole o link do Lalamove aqui"
                                            value={linkInput}
                                            onChange={(e) => setLinkInput(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleSalvarLink(e); }}
                                            bg="white"
                                            isDisabled={salvandoLink}
                                        />
                                        <Button
                                            size="xs"
                                            colorScheme="purple"
                                            onClick={handleSalvarLink}
                                            isLoading={salvandoLink}
                                            flexShrink={0}
                                        >
                                            Salvar
                                        </Button>
                                        {editandoLink && linkRastreioSalvo && (
                                            <Button
                                                size="xs"
                                                variant="ghost"
                                                onClick={(e) => { e.stopPropagation(); setEditandoLink(false); setLinkInput(linkRastreioSalvo); }}
                                                isDisabled={salvandoLink}
                                            >
                                                Cancelar
                                            </Button>
                                        )}
                                    </HStack>
                                </VStack>
                            )}
                        </Box>
                    )}

                    {onDarEntradaEstoque && product.verificationDetails?.status === 2 && (
                        <Button
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            leftIcon={<Icon as={FaWarehouse} />}
                            mt={2}
                            onClick={(e) => { e.stopPropagation(); onDarEntradaEstoque(product); }}
                        >
                            Dar entrada em estoque
                        </Button>
                    )}

                    {onCorrigirImagem && product.verificationDetails?.status === 5 && (
                        <Button
                            size="sm"
                            colorScheme="purple"
                            variant="outline"
                            leftIcon={<Icon as={FaImage} />}
                            mt={2}
                            onClick={(e) => { e.stopPropagation(); onCorrigirImagem(product); }}
                        >
                            Corrigir imagem (foto real)
                        </Button>
                    )}
                </VStack>
            </Box>

            {isLalamove && etapaAtual === 'processamento' && modalLalamoveAberto && (
                <ModalSolicitacaoLalamove
                    isOpen={modalLalamoveAberto}
                    onClose={() => setModalLalamoveAberto(false)}
                    vendaId={product.vendaId}
                    onOrdemCriada={(ordem) => {
                        if (ordem?.share_link && onLinkLalamoveSalvo) {
                            onLinkLalamoveSalvo(product.vendaId, ordem.share_link);
                        }
                    }}
                />
            )}
        </Box>
    );
};
